import {
  useCart,
  useCartLine,
  CartLineQuantityAdjustButton,
  CartLinePrice,
  CartLineQuantity,
  Image,
  Link,
} from '@shopify/hydrogen';
import {useRef} from 'react';

import {Text} from '~/components';
import {useToast} from '~/hooks/useToast';
import {trackClickEvent} from '~/utils/trackClickEvent';
import {productLink} from '../../utils/productLink';

export function CartLineItem() {
  const {linesRemove} = useCart();
  const {id: lineId, quantity, merchandise} = useCartLine();
  const vendor = merchandise?.product?.vendor;

  const {handleCartTotalChange} = useToast();
  const increaseButtonRef = useRef();
  const decreaseButtonRef = useRef();
  const performed = useRef();

  //  have to override the default behaviour of the quantity adjust buttons
  //  to prevent the cart from being re-rendered before the toast is shown
  //  took from here: https://shopify.github.io/hydrogen-v1/components/cart/cartlinequantityadjustbutton
  const handleAdjustOnClick = async (event) => {
    // prevents retriggering
    if (performed.current) {
      performed.current = false;
      return;
    }

    event.preventDefault();

    event?.target?.attributes['aria-label']?.nodeValue == 'Decrease quantity'
      ? handleCartTotalChange(-1)
      : handleCartTotalChange(1);

    await new Promise((r) => setTimeout(r, 500));

    // prevents retriggering
    performed.current = true;

    // trigger button default behavior
    event?.target?.attributes['aria-label']?.nodeValue == 'Decrease quantity'
      ? decreaseButtonRef.current.click()
      : increaseButtonRef.current.click();
  };

  let productURLToVendor = productLink(
    `/products/${merchandise.product.handle}`,
    vendor,
  );

  function CartLineQuantityAdjust({lineId}) {
    return (
      <>
        <label htmlFor={`quantity-${lineId}`} className="">
          Qty:
        </label>
        <div className="qty-actions">
          <CartLineQuantityAdjustButton
            adjust="decrease"
            aria-label="Decrease quantity"
            onClick={() => {
              trackClickEvent('click_cart_line_decrease_quantity');
              handleAdjustOnClick();
            }}
            buttonRef={decreaseButtonRef}
          >
            &#8722;
          </CartLineQuantityAdjustButton>
          <CartLineQuantity as="div" />
          <CartLineQuantityAdjustButton
            adjust="increase"
            aria-label="Increase quantity"
            onClick={() => {
              trackClickEvent('click_cart_line_increase_quantity');
              handleAdjustOnClick();
            }}
            buttonRef={increaseButtonRef}
          >
            &#43;
          </CartLineQuantityAdjustButton>
        </div>
      </>
    );
  }

  return (
    <li key={lineId} className="cart-item cart-item__wrapper">
      <div className="cart-item cart-item__image">
        {/* TODO: implement vendor logo */}
        {/* {vendor && <div className="vendor-logo">({vendor})</div>} */}

        <Image
          width={500}
          height={500}
          alt={`Product image for ${
            merchandise.product?.title ?? 'cart line item'
          }`}
          data={merchandise.image}
          loaderOptions={{
            scale: 2,
            crop: 'center',
          }}
        />
      </div>

      <div className="cart-item cart-item__details">
        <h4 className="item-detail item-detail__title">
          <Link
            to={productURLToVendor}
            onClick={() => {
              trackClickEvent('click_product_link', {
                link: productURLToVendor,
                title: merchandise.product.title,
              });
            }}
          >
            {merchandise.product.title}
          </Link>
        </h4>

        {/* price to only appear in cart overlay */}
        <div className="item-detail__cart-overlay-price">
          <Text>
            <CartLinePrice as="span" />
          </Text>
        </div>

        {/* sku */}
        <div className="item-detail item-detail__sku">
          {merchandise?.sku && <Text>SKU: {merchandise?.sku}</Text>}
        </div>

        {/* options */}
        <div className="item-detail item-detail__option">
          {(merchandise?.selectedOptions || []).map((option) => (
            <Text key={option.name}>
              {option.name}: {option.value}
            </Text>
          ))}
        </div>

        <div className="qty-display">
          <CartLineQuantityAdjust lineId={lineId} quantity={quantity} />
        </div>
      </div>
      <div className="cart-item cart-item__actions">
        <Text>
          <CartLinePrice as="span" />
        </Text>

        <button
          type="button"
          onClick={() => {
            setTimeout(() => {
              linesRemove([lineId]);
            }, 0),
              handleCartTotalChange(-quantity);
          }}
          className="remove-item"
        >
          Remove
        </button>
      </div>
    </li>
  );
}
